export type CarouselScrollDirection = 'prev' | 'next';

export function isRtlElement(element: HTMLElement): boolean {
  return getComputedStyle(element).direction === 'rtl';
}

type RtlScrollType = 'negative' | 'reverse' | 'default';

let rtlScrollTypeCache: RtlScrollType | null = null;

export function getHorizontalScrollDelta(
  direction: CarouselScrollDirection,
  step: number,
  rtl: boolean
): number {
  return direction === 'next' ? (rtl ? -step : step) : rtl ? step : -step;
}

export function getScrollStepFromCard(
  track: HTMLElement,
  cardSelector: string,
  fallbackRatio = 0.25
): number {
  const card = track.querySelector<HTMLElement>(cardSelector);
  if (!card) {
    return Math.min(360, Math.max(220, track.clientWidth * fallbackRatio));
  }
  const styles = getComputedStyle(track);
  const gap = parseFloat(styles.columnGap || styles.gap || '16') || 16;
  return card.offsetWidth + gap;
}

export function getProductRailScrollStep(track: HTMLElement): number {
  return getScrollStepFromCard(track, '.product-card', 0.4);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function detectRtlScrollType(): RtlScrollType {
  if (typeof document === 'undefined') {
    return 'negative';
  }

  const probe = document.createElement('div');
  const child = document.createElement('div');
  probe.dir = 'rtl';
  probe.style.width = '4px';
  probe.style.height = '1px';
  probe.style.overflow = 'scroll';
  child.style.width = '8px';
  child.style.height = '1px';
  probe.appendChild(child);
  document.body.appendChild(probe);

  let type: RtlScrollType;
  if (probe.scrollLeft > 0) {
    type = 'default';
  } else {
    probe.scrollLeft = 1;
    type = probe.scrollLeft === 0 ? 'negative' : 'reverse';
  }

  document.body.removeChild(probe);
  return type;
}

function getRtlScrollType(): RtlScrollType {
  if (rtlScrollTypeCache == null) {
    rtlScrollTypeCache = detectRtlScrollType();
  }
  return rtlScrollTypeCache;
}

/** Normalized scroll offset (0 = start, max = end) across LTR/RTL engines. */
export function getHorizontalScrollPosition(track: HTMLElement): { pos: number; max: number } {
  const max = Math.max(0, track.scrollWidth - track.clientWidth);
  if (max <= 0) {
    return { pos: 0, max: 0 };
  }

  const rtl = isRtlElement(track);
  const sl = track.scrollLeft;

  if (!rtl) {
    return { pos: clamp(sl, 0, max), max };
  }

  switch (getRtlScrollType()) {
    case 'negative':
      return { pos: clamp(-sl, 0, max), max };
    case 'reverse':
      return { pos: clamp(sl, 0, max), max };
    default:
      return { pos: clamp(max - sl, 0, max), max };
  }
}

function scrollHorizontalToPosition(track: HTMLElement, pos: number): void {
  const max = Math.max(0, track.scrollWidth - track.clientWidth);
  const clamped = clamp(pos, 0, max);
  if (max <= 0) {
    return;
  }

  const rtl = isRtlElement(track);
  if (!rtl) {
    track.scrollTo({ left: clamped, behavior: 'smooth' });
    return;
  }

  let left = clamped;
  switch (getRtlScrollType()) {
    case 'negative':
      left = -clamped;
      break;
    case 'reverse':
      left = clamped;
      break;
    default:
      left = max - clamped;
      break;
  }

  track.scrollTo({ left, behavior: 'smooth' });
}

export function scrollElementHorizontal(
  track: HTMLElement,
  direction: CarouselScrollDirection,
  step: number
): void {
  const { pos, max } = getHorizontalScrollPosition(track);
  const delta = direction === 'next' ? step : -step;
  scrollHorizontalToPosition(track, clamp(pos + delta, 0, max));
}

/** Whether the track is scrolled to the logical end (next would not move further). */
export function isAtHorizontalScrollEnd(track: HTMLElement): boolean {
  const { pos, max } = getHorizontalScrollPosition(track);
  return max <= 4 || pos >= max - 4;
}

export function isAtHorizontalScrollStart(track: HTMLElement): boolean {
  const { pos, max } = getHorizontalScrollPosition(track);
  return max <= 4 || pos <= 4;
}

export function scrollHorizontalToStart(track: HTMLElement): void {
  scrollHorizontalToPosition(track, 0);
}

export function scrollHorizontalToEnd(track: HTMLElement): void {
  const max = Math.max(0, track.scrollWidth - track.clientWidth);
  scrollHorizontalToPosition(track, max);
}

/** Scroll with wrap-around at both ends (circular rail). */
export function scrollElementHorizontalCircular(
  track: HTMLElement,
  direction: CarouselScrollDirection,
  step: number
): void {
  const { max } = getHorizontalScrollPosition(track);
  if (max <= 4) {
    return;
  }

  if (direction === 'next' && isAtHorizontalScrollEnd(track)) {
    scrollHorizontalToStart(track);
    return;
  }

  if (direction === 'prev' && isAtHorizontalScrollStart(track)) {
    scrollHorizontalToEnd(track);
    return;
  }

  scrollElementHorizontal(track, direction, step);
}
